import { DatadogProvider, withTimeout } from '@datadog/openfeature-browser';
import { InMemoryProvider, ProviderEvents } from '@openfeature/web-sdk';
import { SpinningLoader } from '@digital-pulse/react';
import {
  OpenFeature,
  OpenFeatureProvider,
  ProviderStatus,
  useBooleanFlagValue,
  useNumberFlagValue,
  useOpenFeatureClientStatus,
  useStringFlagValue
} from '@openfeature/react-sdk';
import React, { createElement, Suspense } from 'react';
import { datadogConfig } from './datadogConfig';

const isServer = typeof window === 'undefined';

export const DATADOG_TIMEOUT = 1000;

if (isServer) {
  // There's no client to run an effect during SSR/static export, so no provider
  // would ever get set. Without this, suspend: true would hang forever waiting
  // for a provider that never arrives. Pages using flags already gate rendering
  // on isHydrated and return null before this default-only provider is seen.
  OpenFeature.setProvider(new InMemoryProvider({}));
} else {
  // Set the real provider directly (not in an effect) so components that suspend
  // on flag reads genuinely wait for Datadog's resolved value before rendering,
  // instead of firing once with a default and again once the real value arrives.
  // Datadog's precompute-assignments call has no timeout by default, so a slow
  // endpoint would hang suspended components indefinitely - bound it explicitly.
  OpenFeature.setProvider(
    new DatadogProvider({
      ...datadogConfig.providerConfig,
      flagConfigurationFetch: withTimeout(globalThis.fetch, DATADOG_TIMEOUT)
    }),
    datadogConfig.evaluationContext
  );

  OpenFeature.getClient().addHandler(ProviderEvents.Error, (details) => {
    console.warn('FeatureFlagWrapper: Datadog provider errored, falling back to defaultValue', details);
  });

  OpenFeature.getClient().addHandler(ProviderEvents.Stale, (details) => {
    console.warn('FeatureFlagWrapper: Datadog provider is serving stale configuration', details);
  });
}

// When Datadog's provider errors (e.g. the flagConfigurationFetch timeout above),
// suspend:true hooks reject and rethrow instead of resolving to defaultValue -
// that would crash the page. Once status is ERROR, stop suspending so the hook
// falls through to its normal (non-suspending) path, which already returns defaultValue.
const useSuspendUntilReady = () => useOpenFeatureClientStatus() !== ProviderStatus.ERROR;

// React's `use()` throws PROVIDER_ERROR as a render error, not a suspense retry -
// Suspense alone can't catch it. Without this boundary the throw propagates
// uncaught past this tree and flag hooks never get a chance to re-render with
// suspend:false and resolve to defaultValue. Catching and re-rendering the same
// children gives every flag hook that retry, now safely non-suspending.
class FeatureFlagErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasErrored: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasErrored: false };
  }

  static getDerivedStateFromError() {
    return { hasErrored: true };
  }

  componentDidCatch(error: unknown) {
    console.warn('FeatureFlagWrapper: caught provider error, retrying with defaults', error);
  }

  render() {
    // key change forces a fresh mount, so hooks re-run from scratch against
    // the now-ERROR provider status instead of resuming a torn-down tree.
    return createElement(React.Fragment, { key: this.state.hasErrored ? 'errored' : 'ok' }, this.props.children);
  }
}

export const useFeatureFlag = (flagName: string, defaultValue: boolean) => {
  const suspend = useSuspendUntilReady();
  return useBooleanFlagValue(flagName, defaultValue, { suspendUntilReady: suspend, suspendWhileReconciling: suspend });
};

export const useStringFlag = (flagName: string, defaultValue: string): string => {
  const suspend = useSuspendUntilReady();
  return useStringFlagValue(flagName, defaultValue, { suspendUntilReady: suspend, suspendWhileReconciling: suspend });
};

export const useNumberFlag = (flagName: string, defaultValue: number): number => {
  const suspend = useSuspendUntilReady();
  return useNumberFlagValue(flagName, defaultValue, { suspendUntilReady: suspend, suspendWhileReconciling: suspend });
};

export const FeatureFlagWrapper = (wrappedComponent: React.ComponentType) => {
   const WrappedComponent = wrappedComponent;
   return function WithFeatureFlags(props: any) {
      return createElement(
        OpenFeatureProvider,
        null,
        createElement(
          FeatureFlagErrorBoundary,
          null,
          createElement(
            Suspense,
            { fallback: createElement(SpinningLoader, { status: 'Loading', size: 'large' }) },
            createElement(WrappedComponent, props)
          )
        )
      );
   };
};
